import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import Main from "containers/Main";
import { themes, createCustomTheme, getSavedThemeColors } from "theme";
import { settings } from "portfolio";
import ReactGA from "react-ga";
import LiveClock from "components/liveClock/LiveClock";
import ChatButton from "components/chatButton/ChatButton";
import ChatModal from "components/chatModal/ChatModal";
import chatbotService from "services/chatbotService";
import { handleError, getUserFriendlyErrorMessage } from "utils/errorHandler";

function App(): React.ReactElement {
  useEffect(() => {
    if (settings.googleTrackingID) {
      ReactGA.initialize(settings.googleTrackingID, {
        testMode: process.env.NODE_ENV === "test",
      });
      ReactGA.pageview(
        globalThis.location.pathname + globalThis.location.search,
      );
    }
  }, []);

  const [theme, setTheme] = useState<string>(() => {
    if (globalThis.window === undefined) return "dark";
    return localStorage.getItem("theme") || "dark";
  });

  const [customColors, setCustomColors] = useState<{
    accentColor: string;
    accentBright: string;
  } | null>(null);

  // Sync dark mode class on <html> for Tailwind darkMode: "class"
  useEffect(() => {
    const root = globalThis.document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    root.classList.toggle("light", theme === "light");
  }, [theme]);

  useEffect(() => {
    const savedColors = getSavedThemeColors();
    if (savedColors) {
      setCustomColors(savedColors);
    }
  }, []);

  const [isChatOpen, setIsChatOpen] = useState(false);
  const sessionIdRef = useRef<string>("");
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

  const currentTheme = useMemo(() => {
    const selectedTheme = themes[theme] || themes.dark;
    if (!customColors) return selectedTheme;

    return createCustomTheme(
      selectedTheme,
      customColors.accentColor,
      customColors.accentBright,
    );
  }, [theme, customColors]);

  // Sync theme accent colors to the CSS variables used across the site.
  useEffect(() => {
    const root = globalThis.document.documentElement;

    root.style.setProperty("--accent", currentTheme.accentColor);
    root.style.setProperty(
      "--accent-dim",
      `color-mix(in srgb, ${currentTheme.accentColor} 15%, transparent)`,
    );
    root.style.setProperty(
      "--accent-glow",
      `color-mix(in srgb, ${currentTheme.accentBright} 40%, transparent)`,
    );
    root.style.setProperty("--brand-accent", currentTheme.accentColor);
    root.style.setProperty("--brand-accent-bright", currentTheme.accentBright);
  }, [currentTheme]);

  // Close chat modal on route change
  useEffect(() => {
    const handleRouteChange = () => {
      setIsChatOpen(false);
    };

    globalThis.addEventListener("hashchange", handleRouteChange);

    return () => {
      globalThis.removeEventListener("hashchange", handleRouteChange);
    };
  }, []);

  const handleColorChange = useCallback(
    (accentColor: string, accentBright: string) => {
      setCustomColors({ accentColor, accentBright });
    },
    [],
  );

  const handleSendMessage = useCallback(
    async (message: string): Promise<string> => {
      try {
        const response = await chatbotService.sendMessage(
          message,
          sessionIdRef.current,
        );
        sessionIdRef.current = response.sessionId;
        return response.response;
      } catch (error) {
        handleError(error, {
          component: "App",
          operation: "handleSendMessage",
        });
        throw new Error(getUserFriendlyErrorMessage(error));
      }
    },
    [],
  );

  const handleContactSubmit = useCallback(
    async (data: {
      name: string;
      email: string;
      message: string;
    }): Promise<boolean> => {
      try {
        const response = await chatbotService.sendContactMessage(data);
        return response.success;
      } catch (error) {
        handleError(error, {
          component: "App",
          operation: "handleContactSubmit",
        });
        return false;
      }
    },
    [],
  );

  return (
    <>
      <Main
        theme={currentTheme}
        setTheme={setTheme}
        onColorChange={handleColorChange}
        isColorPickerOpen={isColorPickerOpen}
        onColorPickerOpenChange={setIsColorPickerOpen}
      />
      <LiveClock theme={currentTheme} />

      <ChatButton
        onClick={() => setIsChatOpen(true)}
        onColorPickerClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
        isChatOpen={isChatOpen}
        theme={customColors || undefined}
        isDarkTheme={theme === "dark"}
      />
      <ChatModal
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        theme={customColors || undefined}
        isDarkTheme={theme === "dark"}
        onSendMessage={handleSendMessage}
        onContact={handleContactSubmit}
      />
    </>
  );
}

export default App;
